/**
 * @fileoverview The host page's settings for the map app (Oct 2026).
 *
 * The Django page (map/templates/map.html) used to set four globals in an inline <script>
 * (globalMapAppConfigPath, globalMapAppStaticPath, globalMapAppWorldDataRoot, globalMapAppKeyStore).
 * The site's Content-Security-Policy refuses inline script (lib/jr/jrdcsp.py), so the page now
 * carries them as data- attributes on an element with id "mapAppConfig":
 *
 *   <div id="mapAppConfig" hidden
 *        data-config-path="/static/data/appConfigDjango.json"
 *        data-static-path="/static/"
 *        data-world-data-root="/media/cbm/"
 *        data-key-store=""></div>
 *
 * Every module that needs one of them asks this module (getMapPageConfig()), so the values are
 * read from the DOM once, whenever first needed -- there is no load-order dependence on the host
 * page having run a script first, and no global. (document.currentScript is null inside an ES
 * module, which is why the element is found by id and not by the script tag.)
 *
 * @module config/pageConfig
 */

/** The id of the page element carrying the data- attributes. */
export const MAP_PAGE_CONFIG_ELEMENT_ID = 'mapAppConfig';

/** The static base used when the page names none (the django STATIC_URL). */
export const DEFAULT_STATIC_PATH = '/static/';

let cachedConfig = null;


/**
 * The host page's settings, read from the #mapAppConfig element the first time they are asked for.
 * A missing element yields empty values (the app then reports the missing config path), never a
 * throw here, so the error surfaces where it is understandable.
 * @returns {{configPath: string, staticPath: string, worldDataRoot: (string|null), keyStore: string}}
 */
export function getMapPageConfig() {
    if (cachedConfig) {
        return cachedConfig;
    }
    const el = document.getElementById(MAP_PAGE_CONFIG_ELEMENT_ID);
    if (!el) {
        console.error(`CasebookMap: the page has no #${MAP_PAGE_CONFIG_ELEMENT_ID} element (map.html carries the app's config paths on it).`);
    }
    const read = (name) => (el ? el.getAttribute(name) : null);
    const worldDataRoot = read('data-world-data-root');
    cachedConfig = {
        configPath: read('data-config-path') || '',
        staticPath: read('data-static-path') || '',
        // null (not '') when the page names no world-data tree, as the old `typeof` check gave
        worldDataRoot: (worldDataRoot !== null) ? worldDataRoot : null,
        keyStore: read('data-key-store') || '',
    };
    return cachedConfig;
}


/**
 * The static base path for lazily imported libraries (pmtiles, pdf.js): the page's value, else
 * DEFAULT_STATIC_PATH -- the same fallback the modules used to apply to the global themselves.
 * @returns {string}
 */
export function getStaticBasePathOrDefault() {
    return getMapPageConfig().staticPath || DEFAULT_STATIC_PATH;
}
